import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useSaveLabRow } from '../../hooks/lab-hooks';
import {
  LabScenario,
  LabScenarioTrigger,
  labSlugify,
  LAB_TABLES,
  LAB_TRIGGER_ACTIONS,
  LAB_TRIGGER_TYPES,
} from '../../support/labService';
import {
  labSchemaKeys,
  labTriggerConditionSchema,
  LAB_JSON_SCHEMAS,
} from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabSuggestedTextField,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder, LabSpecContext } from './LabSpecFields';
import LabStepEditorModal from './LabStepEditorModal';

export type LabTriggerEditorModalProps = {
  open: boolean;
  scenario: LabScenario;
  trigger: LabScenarioTrigger | null;
  onClose: () => void;
  onSaved?: (trigger: LabScenarioTrigger) => void;
};

const newTrigger = (scenario: LabScenario): Partial<LabScenarioTrigger> => ({
  scenario: scenario.id,
  key: '',
  name: '',
  description: '',
  order: 10,
  trigger_type: 'elapsed',
  hold_seconds: 0,
  action: 'enable_steps',
  target_steps: [],
  event_name: '',
  fire_once: true,
  cooldown_seconds: 0,
  enabled: true,
});

/** Which actions need target steps, and which need an event name. */
const NEEDS_TARGETS = ['enable_steps', 'skip_steps'];

/**
 * Author a trigger.
 *
 * Triggers are the only thing that releases a gated step, which makes an `enable_steps`
 * trigger with no targets a step that stays blocked for the length of the lab. The form
 * refuses that rather than letting it happen quietly during an event.
 */
const LabTriggerEditorModal: React.FC<LabTriggerEditorModalProps> = ({
  open,
  scenario,
  trigger,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabScenarioTrigger>>(() => newTrigger(scenario));
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formSeed, setFormSeed] = useState(0);
  /** The nested step editor, for "+ New step" without leaving this form. */
  const [newStep, setNewStep] = useState(false);

  const save = useSaveLabRow<Partial<LabScenarioTrigger> & { id?: number }>(LAB_TABLES.trigger);

  const steps = scenario.steps || [];

  useEffect(() => {
    if (!open) return;

    setDraft(
      trigger ? { ...trigger, target_steps: [...(trigger.target_steps || [])] } : newTrigger(scenario),
    );
    setJsonErrors({});
    setFormError(null);
    setNewStep(false);
    setFormSeed((seed) => seed + 1);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trigger?.id]);

  const set = (patch: Partial<LabScenarioTrigger>) =>
    setDraft((current) => ({ ...current, ...patch }));

  /**
   * The key follows the name until the trigger exists, then stops.
   *
   * Firing one by hand during an event names it by key, and so does every gated step that waits on
   * it, so a rename after the fact would break the thing an author was least likely to re-check.
   */
  const setName = (name: string) => set(trigger ? { name } : { name, key: labSlugify(name) });

  const conditionSchema = labTriggerConditionSchema(draft.trigger_type);

  const specContext = useMemo<LabSpecContext>(
    () => ({
      deviceKeys: (scenario.devices || []).map((device) => device.device_key).filter(Boolean),
      criterionKeys: (scenario.success_criteria || [])
        .map((criterion) => criterion.key)
        .filter(Boolean),
      stepKeys: (scenario.steps || []).map((step) => step.key).filter(Boolean),
    }),
    [scenario.devices, scenario.success_criteria, scenario.steps],
  );

  /** Event names already in use on this section, so a second trigger can emit the same one. */
  const eventNamesInUse = useMemo(() => {
    const names: string[] = [];

    (scenario.triggers || []).forEach((candidate) => {
      const name = (candidate.event_name || '').trim();

      if (name && !names.includes(name)) names.push(name);
    });

    return names;
  }, [scenario.triggers]);

  /**
   * Changing the trigger type drops the condition keys the new type cannot read.
   *
   * The condition means something entirely different per type - `state` is a criterion state on one
   * and a health state on the next - so keys left behind from the old type are unreadable rather
   * than merely unused, and carrying them forward would only show up as an unrecognised-key warning
   * on a form the author never typed into.
   */
  const setTriggerType = (trigger_type: LabScenarioTrigger['trigger_type']) => {
    const schema = labTriggerConditionSchema(trigger_type);
    const known = schema ? labSchemaKeys(schema) : [];
    const current = draft.condition || {};
    const kept: Record<string, any> = {};

    known.forEach((key) => {
      if (current[key] !== undefined) kept[key] = current[key];
    });

    set({ trigger_type, condition: Object.keys(kept).length ? kept : null });
    setJsonErrors((errors) => ({ ...errors, condition: null }));
    setFormSeed((seed) => seed + 1);
  };

  const setJson = (field: keyof LabScenarioTrigger) => (
    value: Record<string, any> | null,
    error: string | null,
  ) => {
    setJsonErrors((current) => ({ ...current, [field]: error }));
    if (!error) set({ [field]: value } as Partial<LabScenarioTrigger>);
  };

  const toggleTarget = (stepId: number) =>
    setDraft((current) => {
      const targets = current.target_steps || [];

      return {
        ...current,
        target_steps: targets.includes(stepId)
          ? targets.filter((id) => id !== stepId)
          : [...targets, stepId],
      };
    });

  const needsTargets = NEEDS_TARGETS.includes(draft.action || '');

  /**
   * What the condition has to say for this type of trigger to ever fire.
   *
   * Named per type rather than checked as "there is a condition": an empty object is a condition,
   * and a progress trigger holding one never reaches its floor because it has none.
   */
  const conditionBlocker = ((): string | null => {
    const condition = draft.condition || {};

    switch (draft.trigger_type) {
      case 'elapsed':
      case 'session_elapsed':
        return Number(condition.seconds) > 0
          ? null
          : 'A wait of zero fires the moment the run opens. If that is what you want, pick "When the run opens" instead - it says so.';
      case 'progress':
        return condition.at_least === undefined || condition.at_least === null
          ? 'A progress trigger with no floor never reaches it, so it never fires.'
          : null;
      case 'criterion_met':
        return String(condition.criterion || '').trim()
          ? null
          : 'This trigger names no criterion, so there is nothing for it to wait on.';
      case 'device_state':
        return String(condition.device_key || '').trim()
          ? null
          : 'This trigger names no device, so there is nothing for it to watch.';
      default:
        return null;
    }
  })();

  const handleSubmit = async () => {
    const firstJsonError = Object.entries(jsonErrors).find(([, error]) => !!error);

    if (firstJsonError) {
      setFormError(`${firstJsonError[0]}: ${firstJsonError[1]}`);
      return;
    }

    if (!draft.name?.trim()) {
      setFormError('A trigger needs a name.');
      return;
    }

    if (!draft.key?.trim()) {
      setFormError(
        'That name derives no key, and firing this by hand during an event names it by key. Give it letters or digits, not punctuation alone.',
      );
      return;
    }

    if (conditionBlocker) {
      setFormError(conditionBlocker);
      return;
    }

    if (needsTargets && !(draft.target_steps || []).length) {
      setFormError(
        draft.action === 'enable_steps'
          ? 'This trigger releases no steps, so any step gated on it stays blocked for the whole lab.'
          : 'This trigger skips no steps, so it does nothing.',
      );
      return;
    }

    if (draft.action === 'emit_event' && !draft.event_name?.trim()) {
      setFormError('An emit-event trigger needs an event name.');
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync(draft);
      onSaved?.(saved as LabScenarioTrigger);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={trigger ? `Edit trigger ${trigger.key}` : `New trigger in ${scenario.name}`}
      size="large"
      scrollable
      submitLabel={trigger ? 'Save trigger' : 'Create trigger'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => setName(evt.target.value)}
          helperText="What a proctor sees in the trigger list."
          fullWidth
        />

        <LabDerivedKey
          value={draft.key || ''}
          frozen={!!trigger}
          label="Trigger key"
          usage="Firing it by hand names it by key, and so does the run log."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          multiline
          minRows={2}
          fullWidth
        />

        <LabFieldRow columns={3}>
          <LabOptionSelect
            label="Fires"
            value={draft.trigger_type}
            options={LAB_TRIGGER_TYPES}
            onChange={setTriggerType}
          />
          <TextField
            label="Hold (seconds)"
            type="number"
            value={draft.hold_seconds ?? 0}
            onChange={(evt) => set({ hold_seconds: Number(evt.target.value) })}
            helperText="The condition has to stay true this long first."
            fullWidth
          />
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => set({ order: Number(evt.target.value) })}
            fullWidth
          />
        </LabFieldRow>

        {conditionSchema ? (
          <LabSpecBuilder
            schema={conditionSchema}
            value={draft.condition}
            onChange={setJson('condition')}
            resetKey={formSeed}
            context={specContext}
          />
        ) : (
          <p className="m-0 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
            {draft.trigger_type === 'manual'
              ? 'Nothing to configure. It fires when somebody fires it, and never on its own.'
              : 'Nothing to configure. It fires when the run opens.'}
          </p>
        )}

        <LabOptionSelect
          label="Action"
          value={draft.action}
          options={LAB_TRIGGER_ACTIONS}
          onChange={(action) => set({ action })}
        />

        {needsTargets && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-700">
                {draft.action === 'skip_steps' ? 'Steps to skip' : 'Steps to release'}
              </div>
              <StbButton role="add" size="xsmall" onClick={async () => setNewStep(true)}>
                + New step
              </StbButton>
            </div>
            <div className="mt-1 max-h-48 overflow-y-auto rounded border border-gray-200 p-2">
              {draft.action === 'enable_steps' && (
                <p className="m-0 mb-1 text-xs text-gray-500">
                  A step only waits for this if it is gated. Releasing one that is not gated changes
                  nothing - it was going to run anyway.
                </p>
              )}
              {steps.length ? (
                steps.map((step) => (
                  <label className="flex items-center gap-2 text-sm" key={step.id}>
                    <input
                      type="checkbox"
                      checked={(draft.target_steps || []).includes(step.id)}
                      onChange={() => toggleTarget(step.id)}
                    />
                    <span>
                      {`${step.key} (${step.phase})`}
                      {step.gated && (
                        <span className="ml-1 text-xs text-gray-500">gated</span>
                      )}
                    </span>
                  </label>
                ))
              ) : (
                <span className="text-sm text-gray-500">This scenario has no steps yet.</span>
              )}
            </div>
          </div>
        )}

        {draft.action === 'emit_event' && (
          <LabSuggestedTextField
            label="Event name"
            value={draft.event_name || ''}
            onChange={(event_name) => set({ event_name })}
            suggestions={eventNamesInUse}
            helpText="Whatever is listening matches on this exact string, so a second trigger emitting the same event should reuse the name rather than a near miss of it."
          />
        )}

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.triggerActionDetail}
          value={draft.action_detail}
          onChange={setJson('action_detail')}
          resetKey={formSeed}
          context={specContext}
        />

        <LabFieldRow>
          <TextField
            label="Cooldown (seconds)"
            type="number"
            value={draft.cooldown_seconds ?? 0}
            onChange={(evt) => set({ cooldown_seconds: Number(evt.target.value) })}
            helperText="Ignored when it only fires once."
            fullWidth
          />
          <div className="flex flex-col justify-end gap-2">
            <LabCheckbox
              label="Fire once"
              checked={!!draft.fire_once}
              onChange={(fire_once) => set({ fire_once })}
            />
            <LabCheckbox
              label="Enabled"
              checked={!!draft.enabled}
              onChange={(enabled) => set({ enabled })}
            />
          </div>
        </LabFieldRow>
      </div>

      {/* Over this form rather than instead of it: the step a trigger releases is usually the reason
          the trigger is being written, and sending an author to the step list to add one would lose
          the condition they just filled in. The new step is ticked on the way back. */}
      <LabStepEditorModal
        open={newStep}
        scenario={scenario}
        step={null}
        onClose={() => setNewStep(false)}
        onSaved={(saved) =>
          setDraft((current) => ({
            ...current,
            target_steps: [...(current.target_steps || []), saved.id],
          }))
        }
      />
    </StbModal>
  );
};

export default LabTriggerEditorModal;
